import * as React from "react";
import { useEffect, useState, lazy, Suspense } from "react";
import { consola } from "consola";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { useUser } from "./hooks/use-user";
import { useIsAdmin, useActiveRole } from "./hooks/use-role";
import { Loader2 } from "lucide-react";
import * as Swetrix from "swetrix";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import websocketService from "./services/websocketService";
import {
  storePreviousPageBeforeLogin,
  getPreviousPage,
  clearPreviousPage,
} from "@/lib/navigation";
import { checkDomainRedirect, getSiteName } from "@/lib/domain";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { EcoLoadingScreen } from "./components/EcoLoadingScreen";
import { CookieBanner } from "./components/CookieBanner";
import { initCookieConsent } from "@/lib/cookies";
import { CleanerMobileNav, CleanerMobileNavSpacer } from "./components/CleanerMobileNav";

// Lazy load page components
const HomePage = lazy(() => import("./pages/HomePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const SignInPage = lazy(() => import("./pages/SignInPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const ClerkCallbackPage = lazy(() => import("./pages/ClerkCallbackPage"));
const SSOCallbackPage = lazy(() => import("./pages/SSOCallbackPage"));
const SelectRolePage = lazy(() => import("./pages/SelectRolePage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProfileCalendarPage = lazy(() => import("./pages/ProfileCalendarPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminMessagesPage = lazy(() => import("./pages/AdminMessagesPage"));
const AdminActivityPage = lazy(() => import("./pages/admin/AdminActivityPage"));
const RoleManagementPage = lazy(() => import("./pages/admin/RoleManagementPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const BookingDetailsPage = lazy(() => import("./pages/BookingDetailsPage"));
const CleanersListPage = lazy(() => import("./pages/CleanersListPage"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SystemHealthDashboard = lazy(
  () => import("./pages/SystemHealthDashboard")
);
const PayoutsPage = lazy(() => import("./pages/PayoutsPage"));
const BookingsListPage = lazy(() => import("./pages/BookingsListPage"));
const CommunityImpactPage = lazy(() => import("./pages/CommunityImpactPage"));
const MyReviewsPage = lazy(() => import("./pages/MyReviewsPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const MessagesPage = lazy(() => import("./pages/Messages"));
const TestMessagingPage = lazy(() => import("./pages/TestMessaging"));
const PropertiesPage = lazy(() => import("@/pages/PropertiesPage"));
const ReferralsPage = lazy(() => import("@/pages/ReferralsPage"));
const ReferralProgramPage = lazy(() => import("@/pages/ReferralProgramPage"));
const HomeownerLandingPage = lazy(() => import("@/pages/HomeownerLandingPage"));
const CleanerLandingPage = lazy(() => import("@/pages/CleanerLandingPage"));
const CleanerJobsQuebecPage = lazy(
  () => import("@/pages/CleanerJobsQuebecPage")
);
const BlogPage = lazy(() => import("./pages/blog"));
const BlogPostPage = lazy(() => import("./pages/blog/[slug]"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Tools pages
const ToolsPage = lazy(() => import("./pages/tools"));
const CleaningTimeCalculator = lazy(() => import("./pages/tools/CleaningTimeCalculator"));
const MoveOutChecklist = lazy(() => import("./pages/tools/MoveOutChecklist"));
const CleaningFrequencyRecommender = lazy(() => import("./pages/tools/CleaningFrequencyRecommender"));
const SpringCleaningEstimator = lazy(() => import("./pages/tools/SpringCleaningEstimator"));

// Define a type for the props
interface OnboardingProps {
  [key: string]: any; // Use any for now, ideally this would be more specific
}

const CleanerOnboarding = lazy(() => import("@/pages/CleanerOnboarding"));

const ClientOnboarding = lazy(() => import("@/pages/ClientOnboarding"));
const CompanyOnboarding = lazy(() => import("@/pages/CompanyOnboarding"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const AppsPage = lazy(() => import("./pages/AppsPage"));
const RegionLandingPage = lazy(() => import("@/pages/RegionLandingPage"));
const ColorSchemeMockupPage = lazy(
  () => import("@/pages/ColorSchemeMockupPage")
);
const ThemeTestPage = lazy(() => import("@/pages/ThemeTestPage"));
const PayoutsTestPage = lazy(() => import("./pages/admin/PayoutsTestPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const ProfileReviewPage = lazy(() => import("./pages/admin/ProfileReviewPage"));
const CleanerAnalyticsPage = lazy(
  () => import("./pages/admin/CleanerAnalyticsPage")
);
const AdminAnalyticsPage = lazy(
  () => import("./pages/admin/AdminAnalyticsPage")
);
// Company pages
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const CompanyEmployeeWizard = lazy(() => import("./pages/CompanyEmployeeWizard"));
const CompanyEmployeeManage = lazy(() => import("./pages/CompanyEmployeeManage"));
const CompanyPublicProfile = lazy(() => import("./pages/CompanyPublicProfile"));
const CompanyProfileSettings = lazy(() => import("./pages/CompanyProfileSettings"));
// Service pages
const RegularCleaningPage = lazy(() => import("./pages/RegularCleaningPage"));
const DeepCleaningPage = lazy(() => import("./pages/DeepCleaningPage"));
const MoveInCleaningPage = lazy(() => import("./pages/MoveInCleaningPage"));
const GarageCleaningPage = lazy(() => import("./pages/GarageCleaningPage"));
import GoogleRoleSelectionPage from "./pages/GoogleRoleSelectionPage";

import { RequireClient } from "@/components/RequireClient";
import RequireAuth from "@/components/RequireAuth";
import { getAuthToken } from "./services/authService";

// Initialize Swetrix only in production to prevent development issues
if (process.env.NODE_ENV === "production") {
  Swetrix.init("vFqFfLCu0yba");
  Swetrix.trackViews();
} else {
  console.log("[DEV] Skipping Swetrix initialization in development mode");
}

// Add the RedirectTo component near the top of the file, before the App component
function RedirectTo({ path }: { path: string }) {
  const [_, setLocation] = useLocation();

  useEffect(() => {
    try {
      // Handle potential empty or invalid paths
      const targetPath = path || "/";
      if (process.env.NODE_ENV === "development") {
        consola.debug(`Redirecting to: ${targetPath}`);
      }
      setLocation(targetPath);
    } catch (error) {
      // Fallback to window.location if wouter navigation fails
      consola.error("Navigation error:", error);
      consola.warn("Using fallback navigation in development mode");
      window.location.href = path || "/";
    }
  }, [path, setLocation]);

  // Return null as this is a utility component
  return null;
}

// Component to handle URL parameters and display notifications
function NotificationHandler() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    // Extract URL parameters
    const searchParams = window.location.search;
    const params = new URLSearchParams(searchParams);
    const message = params.get("message");
    const error = params.get("error");

    // Display success message if present
    if (message) {
      consola.debug("Displaying success toast:", message);
      toast({
        title: t("common.success", "Success"),
        description: message,
      });

      // Remove the message parameter from URL to prevent showing the toast again on refresh
      if (window.history && window.history.replaceState) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("message");
        const newUrl =
          window.location.pathname +
          (newParams.toString() ? `?${newParams.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    }

    // Display error message if present
    if (error) {
      consola.log("Displaying error toast:", error);
      toast({
        variant: "destructive",
        title: t("common.error", "Error"),
        description: error,
      });

      // Remove the error parameter from URL to prevent showing the toast again on refresh
      if (window.history && window.history.replaceState) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("error");
        const newUrl =
          window.location.pathname +
          (newParams.toString() ? `?${newParams.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [location, toast, t]);

  return null;
}

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Create a wrapper component for case-insensitive city routes
function CaseInsensitiveCityRoute() {
  const [location] = useLocation();

  // Check if we're on a city route
  if (location.startsWith("/city/")) {
    // Extract the current city name from the URL
    const currentCityName = location.split("/")[2];

    // Normalize the URL if needed (for display purposes only - the route matching works regardless)
    // This ensures all routing is case insensitive but preserves the original casing for display

    // Return the CityLandingPage component
    return <CityLandingPage />;
  }

  // Fallback - should never happen since this is only rendered on city routes
  return null;
}

function App() {
  const { user, isLoading } = useUser();
  const isAdmin = useIsAdmin();
  const activeRole = useActiveRole();
  const [location, setLocation] = useLocation();
  const { t, i18n } = useTranslation();

  // Initialize Google Analytics tracking
  useGoogleAnalytics();

  // Initialize cookie consent system (Loi 25 compliance)
  useEffect(() => {
    initCookieConsent();
  }, []);

  // Check if domain redirect is needed on initial load
  useEffect(() => {
    // Skip redirection during development
    if (process.env.NODE_ENV !== "production") return;

    // Only attempt redirection once on initial page load
    checkDomainRedirect();

    // Force French language on nettoigo.ca domain
    const hostname = window.location.hostname;
    if (hostname === "nettoigo.ca" && i18n.language !== "fr") {
      i18n.changeLanguage("fr");
    }
  }, [i18n]);

  useEffect(() => {
    // Store the current location when the component mounts or location changes
    // This allows redirecting back after login
    // But DON'T store the homepage as a previous page to avoid redirect loops
    if (location !== "/" && location !== "/login" && location !== "/auth") {
      storePreviousPageBeforeLogin(location);
    }
  }, [location]);

  // Connect to WebSocket server when user logs in
  useEffect(() => {
    let isActive = true;

    const connectWebSocket = async () => {
      if (!user || !isActive) return;

      try {
        consola.log("Initiating WebSocket connection for user:", user.id);
        await websocketService.connect();
        if (isActive) {
          consola.log("WebSocket connection established successfully");
        }
      } catch (error) {
        if (isActive) {
          consola.error("WebSocket connection failed:", error);
          // Individual components will handle their own connections if needed
        }
      }
    };

    connectWebSocket();

    return () => {
      isActive = false;
      websocketService.disconnect();
    };
  }, [user?.id]); // Only reconnect if user ID changes

  if (isLoading) {
    return <EcoLoadingScreen isLoading={true} />;
  }

  // Determine site name based on domain
  const siteName = getSiteName();

  // Routes that don't require authentication
  if (!user) {
    return (
      <HelmetProvider>
        <div className="min-h-screen flex flex-col">
          <NotificationHandler />
          <CookieBanner />
          <Switch>
            <Route>
              <Header siteName={siteName} />
              <main className="flex-grow">
                <Suspense fallback={<PageLoader />}>
                  <Switch>
                    <Route path="/">
                      {() => {
                        if (process.env.NODE_ENV === "development") {
                          consola.log(
                            "[App.tsx] Unauthenticated user accessing / - Rendering HomePage"
                          );
                        }
                        return <HomePage />;
                      }}
                    </Route>
                    <Route
                      path="/homeowners"
                      component={HomeownerLandingPage}
                    />
                    <Route
                      path="/cleaners/join"
                      component={CleanerLandingPage}
                    />
                    <Route
                      path="/devenir-nettoyeur"
                      component={CleanerLandingPage}
                    />
                    <Route
                      path="/emplois-nettoyage-quebec"
                      component={CleanerJobsQuebecPage}
                    />
                    <Route
                      path="/emplois-menage-montreal"
                      component={CleanerJobsQuebecPage}
                    />
                    <Route
                      path="/nettoyeur-independant-quebec"
                      component={CleanerJobsQuebecPage}
                    />
                    <Route path="/about" component={AboutPage} />
                    <Route path="/how-it-works" component={HowItWorksPage} />
                    <Route path="/terms" component={TermsPage} />
                    <Route path="/privacy" component={PrivacyPage} />
                    <Route path="/pricing" component={PricingPage} />
                    <Route path="/faq" component={FAQPage} />
                    <Route path="/gallery" component={GalleryPage} />
                    <Route path="/apps" component={AppsPage} />
                    <Route path="/contact" component={ContactPage} />
                    <Route path="/impact" component={CommunityImpactPage} />
                    <Route path="/cleaners" component={CleanersListPage} />
                    <Route
                      path="/cleaners/:slug"
                      component={PublicProfilePage}
                    />
                    <Route
                      path="/companies/:slug"
                      component={CompanyPublicProfile}
                    />
                    <Route
                      path="/city/:cityName"
                      component={CaseInsensitiveCityRoute}
                    />
                    <Route
                      path="/region/:regionId"
                      component={RegionLandingPage}
                    />
                    <Route
                      path="/forgot-password"
                      component={ForgotPasswordPage}
                    />
                    <Route
                      path="/reset-password/:token"
                      component={ResetPasswordPage}
                    />
                    <Route
                      path="/bookings/:bookingId"
                      component={BookingDetailsPage}
                    />
                    <Route
                      path="/bookings/:id/:action"
                      component={BookingConfirmation}
                    />
                    <Route
                      path="/referral-program"
                      component={ReferralProgramPage}
                    />
                    <Route path="/reviews" component={ReviewsPage} />
                    <Route path="/blog" component={BlogPage} />
                    <Route path="/blog/:slug" component={BlogPostPage} />
                    <Route path="/tools" component={ToolsPage} />
                    <Route path="/tools/cleaning-time-calculator" component={CleaningTimeCalculator} />
                    <Route path="/tools/move-out-checklist" component={MoveOutChecklist} />
                    <Route path="/tools/cleaning-frequency-recommender" component={CleaningFrequencyRecommender} />
                    <Route path="/tools/spring-cleaning-estimator" component={SpringCleaningEstimator} />
                    <Route
                      path="/color-scheme-mockup"
                      component={ColorSchemeMockupPage}
                    />
                    <Route path="/theme-test" component={ThemeTestPage} />
                    <Route
                      path="/services/nettoyage-regulier"
                      component={RegularCleaningPage}
                    />
                    <Route
                      path="/services/nettoyage-profondeur"
                      component={DeepCleaningPage}
                    />
                    <Route
                      path="/services/nettoyage-demenagement"
                      component={MoveInCleaningPage}
                    />
                    <Route
                      path="/services/nettoyage-garage"
                      component={GarageCleaningPage}
                    />
                    <Route path="/book/:cleanerSlug">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access booking page, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/bookings/:bookingId">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access booking details, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/bookings/:id/:action">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access booking confirmation, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/profile">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access profile, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/messages">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access messages, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/messages/:bookingId">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access specific message, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/bookings">
                      {() => {
                        consola.log(
                          "Unauthenticated user trying to access bookings list, redirecting to login"
                        );
                        storePreviousPageBeforeLogin(location);
                        return <Redirect to="/login" />;
                      }}
                    </Route>
                    <Route path="/auth/success">
                      {() => {
                        // Let the server handle the redirect logic
                        window.location.reload();
                        return null;
                      }}
                    </Route>
                    <Route path="/login" component={SignInPage} />
                    <Route path="/register" component={SignUpPage} />
                    <Route path="/auth/clerk-callback" component={ClerkCallbackPage} />
                    <Route path="/sso-callback" component={SSOCallbackPage} />
                    <Route path="/auth" component={AuthPage} />
                    <Route
                      path="/auth/select-role"
                      component={SelectRolePage}
                    />
                    <Route component={NotFoundPage} />
                  </Switch>
                </Suspense>
              </main>
              <CleanerMobileNavSpacer />
              <Footer siteName={siteName} />
              <CleanerMobileNav />
            </Route>
          </Switch>
        </div>
      </HelmetProvider>
    );
  }

  // add a new block for admin routes
  // Show admin layout if user's active role is admin
  if (isAdmin && activeRole === "admin") {
    return (
      <div className="min-h-screen flex flex-col">
        <NotificationHandler />
        <CookieBanner />
        <Header siteName={siteName} />
        <main className="flex-grow">
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/admin" component={AdminDashboard} />
              <Route path="/admin/health" component={SystemHealthDashboard} />
              <Route path="/admin/users" component={UsersPage} />
              <Route path="/admin/roles" component={RoleManagementPage} />
              <Route path="/admin/profile-review" component={ProfileReviewPage} />
              <Route path="/admin/payouts" component={PayoutsTestPage} />
              <Route path="/admin/marketing" component={AdminMarketing} />
              <Route path="/admin/messages" component={AdminMessagesPage} />
              <Route path="/admin/activity" component={AdminActivityPage} />
              <Route path="/admin/analytics" component={AdminAnalyticsPage} />
              <Route
                path="/admin/cleaners/:cleanerId"
                component={CleanerAnalyticsPage}
              />
              <Route path="/payouts" component={PayoutsPage} />
              <Route>
                {() => {
                  // Use a component instead of direct state updates in render
                  return <RedirectTo path="/admin" />;
                }}
              </Route>
            </Switch>
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col">
        <NotificationHandler />
        <CookieBanner />
        <Switch>
          {/* Web routes */}
          <Route>
            <Header siteName={siteName} />
            <main className="flex-grow">
              <Suspense fallback={<PageLoader />}>
                <Switch>
                  <Route path="/" component={HomePage} />
                  <Route path="/about" component={AboutPage} />
                  <Route path="/homeowners" component={HomeownerLandingPage} />
                  <Route path="/cleaners/join" component={CleanerLandingPage} />
                  <Route
                    path="/emplois-nettoyage-quebec"
                    component={CleanerJobsQuebecPage}
                  />
                  <Route
                    path="/emplois-menage-montreal"
                    component={CleanerJobsQuebecPage}
                  />
                  <Route
                    path="/nettoyeur-independant-quebec"
                    component={CleanerJobsQuebecPage}
                  />
                  <Route path="/how-it-works" component={HowItWorksPage} />
                  <Route path="/terms" component={TermsPage} />
                  <Route path="/privacy" component={PrivacyPage} />
                  <Route path="/pricing" component={PricingPage} />
                  <Route path="/faq" component={FAQPage} />
                  <Route path="/gallery" component={GalleryPage} />
                  <Route path="/apps" component={AppsPage} />
                  <Route path="/contact" component={ContactPage} />
                  <Route
                    path="/color-scheme-mockup"
                    component={ColorSchemeMockupPage}
                  />
                  <Route path="/theme-test" component={ThemeTestPage} />
                  <Route path="/impact" component={CommunityImpactPage} />
                  <Route path="/bookings">
                    <RequireAuth>
                      <BookingsListPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/cleaners" component={CleanersListPage} />
                  <Route path="/cleaners/:slug" component={PublicProfilePage} />
                  <Route path="/companies/:slug" component={CompanyPublicProfile} />
                  <Route
                    path="/city/:cityName"
                    component={CaseInsensitiveCityRoute}
                  />
                  <Route
                    path="/region/:regionId"
                    component={RegionLandingPage}
                  />
                  <Route
                    path="/services/nettoyage-regulier"
                    component={RegularCleaningPage}
                  />
                  <Route
                    path="/services/nettoyage-profondeur"
                    component={DeepCleaningPage}
                  />
                  <Route
                    path="/services/nettoyage-demenagement"
                    component={MoveInCleaningPage}
                  />
                  <Route
                    path="/services/nettoyage-garage"
                    component={GarageCleaningPage}
                  />
                  <Route path="/book/:cleanerSlug">
                    <RequireAuth>
                      <BookingPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/profile">
                    <RequireAuth>
                      <ProfilePage />
                    </RequireAuth>
                  </Route>
                  <Route path="/profile/calendar">
                    <RequireAuth>
                      <ProfileCalendarPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/profile/payouts">
                    <RequireAuth>
                      <PayoutsPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/profile/stripe-connect/return">
                    {() => {
                      return <RedirectTo path="/profile" />;
                    }}
                  </Route>
                  <Route path="/profile/stripe-connect/refresh">
                    {() => {
                      return <RedirectTo path="/profile" />;
                    }}
                  </Route>

                  <Route path="/onboarding" component={CleanerOnboarding} />
                  <Route
                    path="/client-onboarding"
                    component={ClientOnboarding}
                  />
                  <Route
                    path="/company-onboarding"
                    component={CompanyOnboarding}
                  />

                  <Route path="/bookings/:bookingId">
                    <RequireAuth>
                      <BookingDetailsPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/bookings/:id/:action">
                    <RequireAuth>
                      <BookingConfirmation />
                    </RequireAuth>
                  </Route>
                  <Route path="/profile/reviews">
                    <RequireAuth>
                      <MyReviewsPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/reviews" component={ReviewsPage} />
                  <Route path="/messages">
                    <RequireAuth>
                      <MessagesPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/messages/:bookingId">
                    <RequireAuth>
                      <MessagesPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/test-messaging" component={TestMessagingPage} />
                  <Route path="/properties">
                    <RequireClient>
                      <PropertiesPage />
                    </RequireClient>
                  </Route>
                  <Route path="/referrals">
                    <RequireAuth>
                      <ReferralsPage />
                    </RequireAuth>
                  </Route>
                  <Route path="/company/dashboard">
                    <RequireAuth>
                      <CompanyDashboard />
                    </RequireAuth>
                  </Route>
                  <Route path="/company/employees/new">
                    <RequireAuth>
                      <CompanyEmployeeWizard />
                    </RequireAuth>
                  </Route>
                  <Route path="/company/employees/:id">
                    <RequireAuth>
                      <CompanyEmployeeManage />
                    </RequireAuth>
                  </Route>
                  <Route path="/company/profile">
                    <RequireAuth>
                      <CompanyProfileSettings />
                    </RequireAuth>
                  </Route>
                  <Route path="/blog" component={BlogPage} />
                  <Route path="/blog/:slug" component={BlogPostPage} />
                  <Route path="/tools" component={ToolsPage} />
                  <Route path="/tools/cleaning-time-calculator" component={CleaningTimeCalculator} />
                  <Route path="/tools/move-out-checklist" component={MoveOutChecklist} />
                  <Route path="/tools/cleaning-frequency-recommender" component={CleaningFrequencyRecommender} />
                  <Route path="/tools/spring-cleaning-estimator" component={SpringCleaningEstimator} />
                  <Route path="/auth/success">
                    {() => {
                      // Redirect to the previous page after successful OAuth login
                      const redirectTo = getPreviousPage();
                      // Clear the stored previous page
                      clearPreviousPage();
                      // Use the RedirectTo component instead
                      return <RedirectTo path={redirectTo} />;
                    }}
                  </Route>
                  <Route
                    path="/region/:regionId"
                    component={RegionLandingPage}
                  />
                  <Route
                    path="/auth/select-role"
                    component={SelectRolePage}
                  />
                  <Route path="/auth/clerk-callback" component={ClerkCallbackPage} />
                  <Route path="/sso-callback" component={SSOCallbackPage} />
                  <Route path="/login">
                    {() => {
                      return <RedirectTo path="/profile" />;
                    }}
                  </Route>
                  <Route path="/register">
                    {() => {
                      return <RedirectTo path="/profile" />;
                    }}
                  </Route>
                  <Route>
                    <div className="container mx-auto py-8 px-4">
                      <h1 className="text-2xl font-bold mb-4">
                        404 - Page non trouvée
                      </h1>
                      <p>La page que vous cherchez n'existe pas.</p>
                    </div>
                  </Route>
                </Switch>
              </Suspense>
            </main>
            {/* Only show footer for routes that aren't onboarding */}
            {location.indexOf("/onboarding") !== 0 &&
              location.indexOf("/client-onboarding") !== 0 &&
              location.indexOf("/company-onboarding") !== 0 && (
                <>
                  <CleanerMobileNavSpacer />
                  <Footer siteName={siteName} />
                </>
              )}
            <CleanerMobileNav />
          </Route>
        </Switch>
      </div>
    </HelmetProvider>
  );
}

export default App;
